<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="8"
          md="8"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <Button
            v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
            :label="isButtonStatus"
            size="md"
            @click="showModal('modal-lg-1')"
          />
        </b-col>
      </b-row>
    </div>
    <h6
      class="section-label mt-1"
      style="font-size: 14px;"
    >
      {{ $t('Cash Withdrawal Table') }}
    </h6>
    <b-table
      :items="cashHandlingLimitList.cashWithdrawal"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(cashWithdrawalLimit)="data">
        {{ toBalance(data.value) }}
      </template>
    </b-table>

    <h5
      class="section-label mt-1"
      style="font-size: 14px;"
    >
      {{ $t('Cash Deposit Table') }}
    </h5>
    <b-table
      :items="cashHandlingLimitList.cashDeposit"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(cashWithdrawalLimit)="data">
        {{ toBalance(data.value) }}
      </template>
    </b-table>

    <!-- modal -->
    <b-modal
      id="modal-lg-1"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="cashHandlingLimitRules">
        <b-form @submit.prevent="handleAddToTable">
          <b-row class="match-height">
            <!-- transactionType -->
            <b-col md="6">
              <SelectField
                id="transaction-type"
                v-model="cashHandlingLimitFormData.transactionType"
                :reduce="option => option.value"
                :label="$t('Transaction Type')"
                :placeholder="$t('Select')"
                :options="translatedTransactionTypeOptions"
                :error-name="$t('Transaction Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- transactionType -->
            <!-- targetCustomerType -->
            <b-col md="6">
              <SelectField
                id="target-customer-type"
                v-model="cashHandlingLimitFormData.targetCustomerType"
                :reduce="option => option.value"
                :label="$t('Target Customer Type')"
                :placeholder="$t('Select')"
                :options="translatedTargetCustomerTypeOptions"
                :error-name="$t('Target Customer Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- targetCustomerType -->

            <!-- channelType -->
            <b-col md="6">
              <SelectField
                id="channel-type"
                v-model="cashHandlingLimitFormData.channelType"
                :reduce="option => option"
                :label="$t('Channel Type')"
                :placeholder="$t('Select')"
                :options="channelTypeOptions"
                :error-name="$t('Channel Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- channelType -->

            <!-- limitPeriod -->
            <b-col md="6">
              <SelectField
                id="limit-period"
                v-model="cashHandlingLimitFormData.limitPeriod"
                :reduce="option => option.value"
                :label="$t('Limit Period')"
                :placeholder="$t('Select')"
                :options="translatedLimitPeriodOptions"
                :error-name="$t('Limit Period')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- limitPeriod -->

            <!-- Cash Withdrawal Limit -->
            <b-col md="6">
              <InputNumberField
                id="withdrawal-limit"
                v-model="cashHandlingLimitFormData.cashWithdrawalLimit"
                :label="$t('Cash Withdrawal Limit')"
                :placeholder="$t('Enter Cash Withdrawal Limit')"
                type="number"
                :error-name="$t('Cash Withdrawal Limit')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Cash Withdrawal Limit -->

            <!-- Excess Over Limit Fee Rate (%) -->
            <b-col md="6">
              <InputField
                id="excess-over-limit"
                v-model="cashHandlingLimitFormData.exceesOverLimitFeeRate"
                :label="$t('Excess Over Limit Fee Rate (%)')"
                :placeholder="$t('Enter Excess Over Limit Fee Rate (%)')"
                type="number"
                :error-name="$t('Excess Over Limit Fee Rate (%)')"
                rules="required|max-value:100|positive"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Excess Over Limit Fee Rate (%) -->

          </b-row>

          <b-row class="match-height">
            <!-- Targeted Transactions -->
            <b-col md="6">
              <SelectField
                id="targeted-transaction"
                v-model="cashHandlingLimitFormData.targetedTransactions"
                :reduce="option => option.value"
                :label="$t('Targeted Transactions')"
                :placeholder="$t('Select')"
                :options="translatedTargetedTransactionsOptions"
                multiple
                :error-name="$t('Targeted Transactions')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Targeted Transactions -->
            <b-col
              v-if="isViewOnly"
              md="6"
              class="mt-2"
            >
              <Button
                :label="$t('Add to Table')"
                size="md"
                @click="handleAddToTable"
              />
            </b-col>
          </b-row>
          <div v-if="isViewOnly">
            <h6 class="section-label mt-3 mb-1">
              {{ $t('Cash Withdrawal Table') }}
            </h6>
            <b-table
              ref="cashWithdrawalRef"
              :items="selectedCashWithdrawal"
              :busy="isBusy"
              :per-page="pageSize"
              :current-page="currentPage"
              responsive
              :fields="translatedCashHandlingLimitTableHeaders"
              primary-key="id"
              show-empty
              :empty-text="$t('No records available')"
              class="position-relative"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner
                    class="align-middle"
                    variant="primary"
                  />
                </div>
              </template>
              <template #cell(SN)="data">
                {{ data.index + 1 }}
              </template>
              <template #cell(cashWithdrawalLimit)="data">
                {{ toBalance(data.value) }}
              </template>
              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <Button
                  :label="$t('Remove')"
                  size="sm"
                  @click="removeCashWithdrawal(data.index)"
                />
              </template>

            </b-table>

            <h6 class="section-label mt-3 mb-1">
              {{ $t('Cash Deposit Table') }}
            </h6>
            <b-table
              ref="cashDepositRef"
              :items="selectedCashDeposit"
              :busy="isBusy"
              :per-page="pageSize"
              :current-page="currentPage"
              responsive
              :fields="translatedCashHandlingLimitTableHeaders"
              primary-key="id"
              show-empty
              :empty-text="$t('No records available')"
              class="position-relative"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner
                    class="align-middle"
                    variant="primary"
                  />
                </div>
              </template>
              <template #cell(SN)="data">
                {{ data.index + 1 }}
              </template>
              <template #cell(cashWithdrawalLimit)="data">
                {{ toBalance(data.value) }}
              </template>
              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <Button
                  :label="$t('Remove')"
                  size="sm"
                  @click="removeCashDeposit(data.index)"
                />
              </template>
            </b-table>
          </div>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal, BSpinner, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import getStatusColor from '../../../../../helpers/getStatusColor'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import { toBalance, toKobo } from '../../../../../helpers/formatAmount'
import Button from '../../../../components/button/Button.vue'

const transactionTypeOptions = [
  { value: 'Cash Deposits', label: 'Cash Deposits' },
  { value: 'Cash Withdrawals', label: 'Cash Withdrawals' },
]

const targetCustomerTypeOptions = [
  { value: 'Individual', label: 'Individual' },
  { value: 'Organization', label: 'Organization' },
]

const limitPeriodOptions = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
]

const targetedTransactionsOptions = [
  { value: 'CHQW', label: 'Cheque Withdrawal' },
  { value: 'CHQC', label: 'Cheque Clearing' },
  { value: 'CHQD', label: 'Cheque deposit' },
  { value: 'CSHD', label: 'Cash deposit' },
  { value: 'CSHW', label: 'Cash Withdrawal' },
  { value: 'JRNL', label: 'Journal Posting' },
  { value: 'BCSH', label: 'Buy Cash' },
  { value: 'SCSH', label: 'Sell Cash' },
  { value: 'IBRH', label: 'Inter-branch' },
  { value: 'LTRF', label: 'Local Transfer' },
  { value: 'CLOS', label: 'Account Closure' },
  { value: 'FAAQ', label: 'Fixed Asset Acquisition' },
  { value: 'FADP', label: 'Fixed Asset Depreciation' },
  { value: 'FACT', label: 'Value Added Tax Deduction' },
  { value: 'FAAD', label: 'Fixed Asset Accumulated Depreciation @ Liquidation' },
  { value: 'FALQ', label: 'Fixed Asset Liquidation' },
  { value: 'VATD', label: 'Value Added Tax Deduction' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    InputNumberField,
    SelectField,
    Button,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isViewOnly: true,
      toBalance,
      required,
      isButtonStatus: this.$t('Add'),
      getStatusColor,
      modalTitle: this.$t('Create Cash Handling Limit'),
      okTitle: this.$t('Save'),
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'targetCustomerType', label: 'Customer Type' },
        { key: 'channelType', label: 'Channel Type' },
        { key: 'limitPeriod', label: 'Limit Period' },
        { key: 'cashWithdrawalLimit', label: 'Cash Limit' },
        { key: 'exceesOverLimitFeeRate', label: 'Fee Rate' },
        { key: 'targetedTransactions', label: 'Targeted Trnx Codes' },
      ],
      cashHandlingLimitTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'targetCustomerType', label: 'Customer Type' },
        { key: 'channelType', label: 'Channel Type' },
        { key: 'limitPeriod', label: 'Limit Period' },
        { key: 'cashWithdrawalLimit', label: 'Cash Limit' },
        { key: 'exceesOverLimitFeeRate', label: 'Fee Rate' },
        { key: 'targetedTransactions', label: 'Targeted Trnx Codes' },
        { key: 'actions', label: 'Actions' },
      ],
      cashHandlingLimitFormData: {
        transactionType: '',
        targetCustomerType: '',
        cashWithdrawalLimit: '',
        channelType: '',
        limitPeriod: '',
        exceesOverLimitFeeRate: '',
        targetedTransactions: '',
      },
      selectedCashWithdrawal: [],
      selectedCashDeposit: [],
      // transactionTypeOptions: [
      //   'Cash Deposits',
      //   'Cash Withdrawals',
      // ],
      // targetCustomerTypeOptions: [
      //   'Individual',
      //   'Organization',
      // ],
      channelTypeOptions: [
        'OTC',
        'POS',
        'ATM',
      ],
      // limitPeriodOptions: [
      //   'Daily',
      //   'Weekly',
      //   'Monthly',
      // ],
      // targetedTransactionsOptions: [
      //   { value: 'CHQW', label: 'Cheque Withdrawal' },
      //   { value: 'CHQC', label: 'Cheque Clearing' },
      //   { value: 'CHQD', label: 'Cheque deposit' },
      //   { value: 'CSHD', label: 'Cash deposit' },
      //   { value: 'CSHW', label: 'Cash Withdrawal' },
      //   { value: 'JRNL', label: 'Journal Posting' },
      //   { value: 'BCSH', label: 'Buy Cash' },
      //   { value: 'SCSH', label: 'Sell Cash' },
      //   { value: 'IBRH', label: 'Inter-branch' },
      //   { value: 'LTRF', label: 'Local Transfer' },
      //   { value: 'CLOS', label: 'Account Closure' },
      //   { value: 'FAAQ', label: 'Fixed Asset Acquisition' },
      //   { value: 'FADP', label: 'Fixed Asset Depreciation' },
      //   { value: 'FACT', label: 'Value Added Tax Deduction' },
      //   { value: 'FAAD', label: 'Fixed Asset Accumulated Depreciation @ Liquidation' },
      //   { value: 'FALQ', label: 'Fixed Asset Liquidation' },
      //   { value: 'VATD', label: 'Value Added Tax Deduction' },
      // ],
      cashHandlingLimitList: {
        cashWithdrawal: [],
        cashDeposit: [],
      },
      toDateWithYear,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedCashHandlingLimitTableHeaders() {
      return this.cashHandlingLimitTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionTypeOptions() {
      return transactionTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTargetCustomerTypeOptions() {
      return targetCustomerTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedLimitPeriodOptions() {
      return limitPeriodOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTargetedTransactionsOptions() {
      return targetedTransactionsOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  mounted() {
    this.fetchCashHandlingLimit()
  },
  methods: {
    ...mapActions({
      getCashHandlingLimit: 'operations/getCashHandlingLimit',
      updateCashHandlingLimit: 'operations/updateCashHandlingLimitAction',
    }),
    async handleAddToTable() {
      this.$refs.cashHandlingLimitRules.validate().then(async success => {
        if (success) {
          if (this.cashHandlingLimitFormData.transactionType === 'Cash Withdrawals') {
            this.selectedCashWithdrawal.push({
              ...this.cashHandlingLimitFormData,
              cashWithdrawalLimit: toKobo(this.cashHandlingLimitFormData.cashWithdrawalLimit),
              exceesOverLimitFeeRate: Number(this.cashHandlingLimitFormData.exceesOverLimitFeeRate),
              targetedTransactions: this.cashHandlingLimitFormData.targetedTransactions.map(item => item.value).toString(),
            })
            this.alertSuccess({ text: this.$t('A record added to Cash Withdrawals table successful') })
          } else {
            this.selectedCashDeposit.push({
              ...this.cashHandlingLimitFormData,
              cashWithdrawalLimit: toKobo(this.cashHandlingLimitFormData.cashWithdrawalLimit),
              exceesOverLimitFeeRate: Number(this.cashHandlingLimitFormData.exceesOverLimitFeeRate),
              targetedTransactions: this.cashHandlingLimitFormData.targetedTransactions.map(item => item.value).toString(),
            })
            this.alertSuccess({ text: this.$t('A record added to Cash Deposit table successful') })
          }
        }
      })
    },
    removeCashWithdrawal(index) {
      this.selectedCashWithdrawal.splice(index, 1)
    },
    removeCashDeposit(index) {
      this.selectedCashDeposit.splice(index, 1)
    },

    async handleSubmit() {
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('this Cash Handling Limit?')
      this.confirm(this.cashHandlingLimitAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
    },
    async cashHandlingLimitAction() {
      const yourRequest = this.$t('Your request to')
      try {
        this.isLoading = true
        const response = await this.updateCashHandlingLimit({
          config: {
            cashWithdrawal: [
              ...this.selectedCashWithdrawal,
            ],
            cashDeposit: [
              ...this.selectedCashDeposit,
            ],
          },
        })
        const { status } = await response
        if (status === 200) {
          this.fetchCashHandlingLimit()
          this.hideModal('modal-lg-1')
          const successText = this.$t('Cash Handling Limit was successful')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.hideModal('modal-lg-1')
        const reasonText = this.$t('Cash Handling Limit failed. Reason:')
        this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${reasonText} ${error}` })
      } finally {
        this.isLoading = false
      }
    },
    async fetchCashHandlingLimit() {
      try {
        this.isBusy = true
        const response = await this.getCashHandlingLimit()
        const { status, data } = response
        if (status === 200 && data !== null) {
          this.isButtonStatus = data.cashWithdrawal.length || data.cashDeposit.length ? this.$t('Update') : this.$t('Add')
          this.cashHandlingLimitList.cashWithdrawal = data.cashWithdrawal
          this.cashHandlingLimitList.cashDeposit = data.cashDeposit
          this.selectedCashWithdrawal = data.cashWithdrawal
          this.selectedCashDeposit = data.cashDeposit
          this.isBusy = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.cashHandlingLimitFormData.transactionType = ''
      this.cashHandlingLimitFormData.targetCustomerType = ''
      this.cashHandlingLimitFormData.cashWithdrawalLimit = ''
      this.cashHandlingLimitFormData.channelType = ''
      this.cashHandlingLimitFormData.limitPeriod = ''
      this.cashHandlingLimitFormData.exceesOverLimitFeeRate = ''
      this.cashHandlingLimitFormData.targetedTransactions = ''
      this.isLoading = false
      this.buttonLabel = this.$t('Save')
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
